import React, { useEffect, useMemo } from "react";
import {
  Alert,
  AlertTitle,
  CssBaseline,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import "leaflet/dist/leaflet.css";
import "@fontsource/cairo/400.css";
import "@fontsource/cairo/600.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

import SearchAndFilters from "./components/SearchAndFilters";
import StatsPanel from "./components/StatsPanel";
import RouteBoard from "./components/RouteBoard";
import TrainBoard from "./components/TrainBoard";
import StationList from "./components/StationList";
import NetworkMap from "./components/NetworkMap";
import ActiveFiltersBar from "./components/ActiveFiltersBar";
import SettingsPanel from "./components/SettingsPanel";
import useNetworkData from "./hooks/useNetworkData";
import { useSettings } from "./contexts/SettingsContext";
import { useTranslation } from "./i18n/useTranslation";
import "./App.css";
import irrLogo from "./assets/irr.gif";
import { TRAIN_TYPE_ORDER } from "./data/staticNetwork";

const HOURS_TO_MS = 60 * 60 * 1000;

const App = () => {
  const { settings, updateFilters, updateSettings } = useSettings();
  const {
    filters,
    mapLayer,
    drawLines,
    refreshIntervalSeconds,
    offlineThresholdHours,
    hideTrainProgress,
  } = settings;
  const { query, type, selectedRouteIds, hideOffline } = filters;

  const refreshIntervalMs = refreshIntervalSeconds * 1000;

  const {
    routes,
    trains,
    totals,
    trainsByRoute,
    trainTypeTotals,
    trainTypeMeta,
    loading,
    error,
    refresh,
    lastUpdated,
    isUsingFallback,
  } = useNetworkData({ refreshIntervalMs });

  const {
    t,
    translateRouteName,
    translateRouteSubtitle,
    translateTrainType,
    direction,
    language,
  } = useTranslation();

  const theme = useMemo(
    () =>
      createTheme({
        direction,
        palette: {
          mode: "light",
          primary: {
            main: "#1565d8",
          },
          background: {
            default: "#f4f6fb",
          },
        },
        typography: {
          fontFamily: "'Cairo', 'Roboto', sans-serif",
        },
        shape: {
          borderRadius: 8,
        },
      }),
    [direction]
  );

  const normalizedRoutes = useMemo(
    () =>
      routes.map((route) => ({
        ...route,
        displayName: translateRouteName(route.id, route.name),
        displaySubtitle: translateRouteSubtitle(route.id, route.subtitle),
      })),
    [routes, translateRouteName, translateRouteSubtitle]
  );

  const searchText = query.trim().toLowerCase();
  const selectedRoutesSet = useMemo(
    () => new Set(selectedRouteIds),
    [selectedRouteIds]
  );
  const hasRouteFilter = selectedRouteIds.length > 0;

  const normalizedTrainTypeMeta = useMemo(() => {
    return Object.entries(trainTypeMeta).reduce((acc, [key, meta]) => {
      const translationKey = meta.localizationKey || key;
      const { label, description } = translateTrainType(translationKey, meta.label);
      acc[key] = {
        ...meta,
        label,
        description,
        localizationKey: translationKey,
      };
      return acc;
    }, {});
  }, [trainTypeMeta, translateTrainType]);

  const trainTypeOptions = useMemo(() => {
    const ordered = TRAIN_TYPE_ORDER.filter((key) => normalizedTrainTypeMeta[key]).map(
      (key) => ({
        value: key,
        label: normalizedTrainTypeMeta[key].label,
      })
    );

    const extras = Object.keys(normalizedTrainTypeMeta)
      .filter((key) => !TRAIN_TYPE_ORDER.includes(key))
      .sort((a, b) =>
        normalizedTrainTypeMeta[a].label.localeCompare(
          normalizedTrainTypeMeta[b].label,
          undefined,
          { sensitivity: "base" }
        )
      )
      .map((key) => ({
        value: key,
        label: normalizedTrainTypeMeta[key].label,
      }));

    return [...ordered, ...extras];
  }, [normalizedTrainTypeMeta]);

  const effectiveTypeFilter = useMemo(() => {
    if (type === "all") {
      return "all";
    }

    if (normalizedTrainTypeMeta[type]) {
      return type;
    }

    const legacyMap = {
      "1": "passenger",
      "2": "freight",
      service: "other",
    };

    const normalized = legacyMap[type];

    if (normalized && normalizedTrainTypeMeta[normalized]) {
      return normalized;
    }

    return "all";
  }, [type, normalizedTrainTypeMeta]);

  useEffect(() => {
    if (type === effectiveTypeFilter) {
      return;
    }

    updateFilters({ type: effectiveTypeFilter });
  }, [type, effectiveTypeFilter, updateFilters]);

  const normalizedTrains = useMemo(() => {
    const now = Date.now();
    return trains.map((train) => {
      const fallbackTypeKey =
        typeof train.typeId === "string"
          ? train.typeId
          : train.typeId === 1
          ? "passenger"
          : train.typeId === 2
          ? "freight"
          : "other";
      const typeKey =
        (typeof train.type?.localizationKey === "string" && train.type.localizationKey) ||
        fallbackTypeKey;
      const { label: typeLabel, description: typeDescription } = translateTrainType(
        typeKey,
        train.type?.label
      );
      const lastMessageAt =
        typeof train.lastMessageAt === "number"
          ? train.lastMessageAt
          : typeof train.lastUpdated === "number"
          ? train.lastUpdated
          : now;
      const isOffline = now - lastMessageAt > offlineThresholdHours * HOURS_TO_MS;

      return {
        ...train,
        routeName: translateRouteName(train.routeId, train.routeName),
        type: {
          ...train.type,
          label: typeLabel,
          description: typeDescription,
          localizationKey: typeKey,
        },
        lastMessageAt,
        isOffline,
      };
    });
  }, [trains, translateTrainType, translateRouteName, offlineThresholdHours]);

  const visibleRoutes = useMemo(() => {
    if (!hasRouteFilter) {
      return normalizedRoutes;
    }
    return normalizedRoutes.filter((route) => selectedRoutesSet.has(route.id));
  }, [normalizedRoutes, hasRouteFilter, selectedRoutesSet]);

  const filteredTrains = useMemo(() => {
    return normalizedTrains.filter((train) => {
      const matchesRoute = !hasRouteFilter || selectedRoutesSet.has(train.routeId);
      const matchesType =
        effectiveTypeFilter === "all" ||
        train.type?.localizationKey === effectiveTypeFilter ||
        train.typeId === effectiveTypeFilter ||
        (typeof train.typeId !== "string" &&
          ((effectiveTypeFilter === "passenger" && train.typeId === 1) ||
            (effectiveTypeFilter === "freight" && train.typeId === 2)));
      const matchesSearch =
        !searchText ||
        [train.code, train.name, train.stationName, train.routeName]
          .join(" ")
          .toLowerCase()
          .includes(searchText);
      const matchesOffline = hideOffline ? !train.isOffline : true;
      return matchesRoute && matchesType && matchesSearch && matchesOffline;
    });
  }, [
    normalizedTrains,
    hasRouteFilter,
    selectedRoutesSet,
    effectiveTypeFilter,
    searchText,
    hideOffline,
  ]);

  const computedStatusMetrics = useMemo(() => {
    let online = 0;
    let inStationsAll = 0;
    let inStationsActive = 0;

    normalizedTrains.forEach((train) => {
      const isOnline = !train.isOffline;
      if (train.isInsideKnownGeofence) {
        inStationsAll += 1;
        if (isOnline) {
          inStationsActive += 1;
        }
      }
      if (isOnline) {
        online += 1;
      }
    });

    const total = normalizedTrains.length;
    const offlineCount = total - online;

    return {
      online,
      offline: offlineCount,
      inStationsAll,
      outStationsAll: total - inStationsAll,
      inStationsActive,
      outStationsActive: online - inStationsActive,
    };
  }, [normalizedTrains]);

  const stationTrainCounts = useMemo(() => {
    const counts = new Map();
    filteredTrains.forEach((train) => {
      if (train.isInsideKnownGeofence && train.stationId) {
        const current = counts.get(train.stationId) || 0;
        counts.set(train.stationId, current + 1);
      }
    });
    return counts;
  }, [filteredTrains]);

  const unassignedRouteLabel = t("train.unassignedRoute");

  const fleetSections = useMemo(() => {
    if (!filteredTrains.length) {
      return [];
    }

    const mapByRoute = new Map();

    const ensureGroup = (routeId, routeName) => {
      if (!mapByRoute.has(routeId)) {
        mapByRoute.set(routeId, { routeId, routeName, trains: [] });
      }
      return mapByRoute.get(routeId);
    };

    const getDisplayName = (train) =>
      (train.name && train.name.trim()) || train.code || String(train.id);

    filteredTrains.forEach((train) => {
      const key = train.routeId ?? "unassigned";
      const name =
        train.routeName && train.routeName.trim() ? train.routeName : unassignedRouteLabel;
      const group = ensureGroup(key, name);
      group.trains.push(train);
    });

    const sortByName = (a, b) =>
      getDisplayName(a).localeCompare(getDisplayName(b), undefined, {
        numeric: true,
        sensitivity: "base",
      });

    const sections = [];

    normalizedRoutes.forEach((route) => {
      const group = mapByRoute.get(route.id);
      if (!group) {
        return;
      }

      const online = group.trains.filter((train) => !train.isOffline).sort(sortByName);
      const offline = group.trains.filter((train) => train.isOffline).sort(sortByName);
      sections.push({
        routeId: route.id,
        routeName: route.displayName,
        online,
        offline,
      });
      mapByRoute.delete(route.id);
    });

    if (mapByRoute.has("unassigned")) {
      const group = mapByRoute.get("unassigned");
      const online = group.trains.filter((train) => !train.isOffline).sort(sortByName);
      const offline = group.trains.filter((train) => train.isOffline).sort(sortByName);
      sections.push({
        routeId: "unassigned",
        routeName: unassignedRouteLabel,
        online,
        offline,
      });
    }

    return sections;
  }, [filteredTrains, normalizedRoutes, unassignedRouteLabel]);

  const visibleStations = useMemo(() => {
    const seen = new Set();
    const stations = [];

    visibleRoutes.forEach((route) => {
      route.stations.forEach((station) => {
        if (seen.has(station.id)) {
          return;
        }

        seen.add(station.id);
        stations.push(station);
      });
    });

    if (!searchText) {
      return stations;
    }

    return stations.filter((station) =>
      [station.name, station.code, station.routeName]
        .join(" ")
        .toLowerCase()
        .includes(searchText)
    );
  }, [visibleRoutes, searchText]);

  const routeOptions = useMemo(
    () => normalizedRoutes.map((route) => ({ value: route.id, label: route.displayName })),
    [normalizedRoutes]
  );

  const filteredRouteSummaries = useMemo(() => {
    return normalizedRoutes.filter((route) => {
      if (hasRouteFilter && !selectedRoutesSet.has(route.id)) {
        return false;
      }

      if (!searchText) {
        return true;
      }

      const routeText = [route.displayName, route.displaySubtitle]
        .join(" ")
        .toLowerCase();

      if (routeText.includes(searchText)) {
        return true;
      }

      return route.stations.some((station) =>
        [station.name, station.code].join(" ").toLowerCase().includes(searchText)
      );
    });
  }, [normalizedRoutes, hasRouteFilter, selectedRoutesSet, searchText]);

  const activeFilters = useMemo(() => {
    const items = [];

    if (query.trim()) {
      items.push({
        key: "search",
        label: t("filters.search", { query: query.trim() }),
        onRemove: () => updateFilters({ query: "" }),
      });
    }

    if (hasRouteFilter) {
      const labels = normalizedRoutes
        .filter((route) => selectedRoutesSet.has(route.id))
        .map((route) => route.displayName)
        .join(", ");
      items.push({
        key: "routes",
        label: t("filters.selectedRoutes", { label: labels }),
        onRemove: () => updateFilters({ selectedRouteIds: [] }),
      });
    }

    if (effectiveTypeFilter !== "all") {
      const meta = normalizedTrainTypeMeta[effectiveTypeFilter];
      const label = meta ? meta.label : effectiveTypeFilter;
      items.push({
        key: `type-${effectiveTypeFilter}`,
        label: t("filters.type", { label }),
        onRemove: () => updateFilters({ type: "all" }),
      });
    }

    if (hideOffline) {
      items.push({
        key: "offline",
        label: t("filters.offlineHidden"),
        onRemove: () => updateFilters({ hideOffline: false }),
      });
    }

    return items;
  }, [
    query,
    t,
    updateFilters,
    hasRouteFilter,
    normalizedRoutes,
    selectedRoutesSet,
    effectiveTypeFilter,
    normalizedTrainTypeMeta,
    hideOffline,
  ]);

  const formattedLastUpdated = lastUpdated
    ? lastUpdated.toLocaleString(language === "ar" ? "ar-IQ" : undefined)
    : "";

  const handleToggleRoute = (routeId) => {
    if (!routeId) {
      updateFilters({ selectedRouteIds: [] });
      return;
    }

    if (selectedRouteIds.length === 1 && selectedRouteIds[0] === routeId) {
      updateFilters({ selectedRouteIds: [] });
      return;
    }

    updateFilters({ selectedRouteIds: [routeId] });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box className="app-shell">
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
          <Stack spacing={4}>
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              alignItems={{ xs: "stretch", lg: "flex-start" }}
              justifyContent="space-between"
            >
              <Stack spacing={1.5} flex={1} alignItems={{ xs: "flex-start", md: "flex-start" }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box component="img" src={irrLogo} alt={t("app.title")} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {t("app.title")}
                  </Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  {t("app.subtitle")}
                </Typography>
                {loading && <LinearProgress color="primary" />}
                {!loading && lastUpdated && (
                  <Typography variant="caption" color="text.secondary">
                    {`${t("app.lastUpdated")}: ${formattedLastUpdated} · ${t("app.refreshInterval", {
                      value: refreshIntervalSeconds,
                    })}`}
                  </Typography>
                )}
              </Stack>

              <Box
                sx={{
                  width: { xs: "100%", md: 340 },
                  alignSelf: {
                    xs: "stretch",
                    lg: direction === "rtl" ? "flex-start" : "flex-end",
                  },
                }}
              >
                <SettingsPanel
                  mapLayer={mapLayer}
                  onMapLayerChange={(value) => updateSettings({ mapLayer: value })}
                  drawLines={drawLines}
                  onDrawLinesChange={(value) => updateSettings({ drawLines: value })}
                  language={language}
                  onLanguageChange={(value) => updateSettings({ language: value })}
                  refreshIntervalSeconds={refreshIntervalSeconds}
                  onRefreshIntervalChange={(value) =>
                    updateSettings({ refreshIntervalSeconds: value })
                  }
                  offlineThresholdHours={offlineThresholdHours}
                  onOfflineThresholdChange={(value) =>
                    updateSettings({ offlineThresholdHours: value })
                  }
                  hideOffline={hideOffline}
                  onHideOfflineChange={(value) => updateFilters({ hideOffline: value })}
                  hideTrainProgress={hideTrainProgress}
                  onHideTrainProgressChange={(value) =>
                    updateSettings({ hideTrainProgress: value })
                  }
                />
              </Box>
            </Stack>

            {error && (
              <Alert
                severity={isUsingFallback ? "warning" : "error"}
                action={
                  <Button color="inherit" size="small" onClick={refresh}>
                    {t("app.retry")}
                  </Button>
                }
              >
                <AlertTitle>Wialon</AlertTitle>
                {error.message}
              </Alert>
            )}

            <SearchAndFilters
              query={query}
              onQueryChange={(value) => updateFilters({ query: value })}
              typeFilter={effectiveTypeFilter}
              typeOptions={trainTypeOptions}
              onTypeFilterChange={(value) => updateFilters({ type: value })}
              routeOptions={routeOptions}
              selectedRoutes={selectedRouteIds}
              onRouteChange={(value) => updateFilters({ selectedRouteIds: value })}
            />

            <ActiveFiltersBar filters={activeFilters} />

            <StatsPanel
              totals={totals}
              typeBreakdown={trainTypeTotals}
              trainTypeMeta={normalizedTrainTypeMeta}
              statusMetrics={computedStatusMetrics}
            />

            <RouteBoard
              routes={filteredRouteSummaries}
              selectedRoutes={selectedRouteIds}
              onToggleRoute={handleToggleRoute}
              onClearRoutes={() => updateFilters({ selectedRouteIds: [] })}
              trainsByRoute={trainsByRoute}
            />

            <Divider />

            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} lg={7}>
                <Stack spacing={3}>
                  <NetworkMap
                    routes={visibleRoutes}
                    allRoutes={normalizedRoutes}
                    trains={filteredTrains}
                    mapLayer={mapLayer}
                    onMapLayerChange={(value) => updateSettings({ mapLayer: value })}
                    drawLines={drawLines}
                    onDrawLinesChange={(value) => updateSettings({ drawLines: value })}
                    selectedRouteIds={selectedRouteIds}
                    onToggleRoute={handleToggleRoute}
                    onClearRoutes={() => updateFilters({ selectedRouteIds: [] })}
                    stationTrainCounts={stationTrainCounts}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <StationList
                  stations={visibleStations}
                  selectedRoutes={selectedRouteIds}
                  onRouteToggle={handleToggleRoute}
                />
              </Grid>
            </Grid>

          <Stack spacing={2.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t("stats.fleetComposition")}
            </Typography>
            <TrainBoard
              sections={fleetSections}
              hideTrainProgress={hideTrainProgress}
            />
          </Stack>
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default App;
